import { defineStore } from 'pinia'
import makeAuthenticatedAPICall from '@/utils/makeAuthenticatedAPICall'
import { fetchCategoryFiles } from '@/utils/getUserUploadsAlready'
import { sanitizeDisplayAs } from '@/utils/sanitizeText'

export const useRecordWizardStore = defineStore('record-wizard', {
  state: () => ({
    locutors: [],
    selectedLocutorId: null,
    selectedLanguageId: null,
    hasSelectedNewLocuter: null,
    isEditingLocutor: false,
    newLocuter: {
      name: '',
      is_main_locutor: false,
      license: null,
      gender: null, // Undeclared by default
      can_be_deleted: false,
      languages_used: [],
      place_of_residence: '',
      year_of_birth: null,
      age_group: null,
    },
    items: [],
    recordings: {},
    apiCallError: '',
    shareableListId: undefined,
    userUploadsAlready: [], // as of now assumes single wikimedian user per device
    userUploadsItemsForCurrentLocutorAndLanguage: [], // Add this line
    uploadRetryCounter: 0,
    visible: {
      locutorGender: 1,
      generatorSentenceList: 1,
      generatorLocalList: 1,
      generatorNearby: 1,
      generatorWikimediaCategory: 1,
      generatorExternalTools: 1, 
      generatorLexeme: 1,
      generatorTatoeba: 0,
      downloadLocally: 0,
    },
    warning: ''
  }),
  getters: {
    locutor: (state) => {
      return state.hasSelectedNewLocuter
        ? state.newLocuter
        : (Array.isArray(state.locutors) ? state.locutors.find((locutor) => locutor.id === state.selectedLocutorId) : undefined)
    },
    hasRecordings: (state) => Object.values(state.recordings).length > 0,
    availableLanguages() {
      if (!this.locutor) return []
      return this.locutor['languages_used']
    },
    sortedItems: (state) => {
      return [...state.items].sort((a, b) => (a.position || 0) - (b.position || 0))
    },
  },
  actions: {
    initializeVisibleElements() {
      // Control visibility of tools based on URL parameters or store defaults
      const params = new URLSearchParams(window.location.search);

      const parseBoolean = (value) => {
        console.log("parseBoolean input:", value);
        if (value === '1' || value === 'true') return 1;
        if (value === '0' || value === 'false') return 0;
        console.log("URL parameter has invalid value: ", value);
        return null; // Invalid value, don't change the default
      };
      // default = visible
      if (params.has('gender')) {
        this.visible.locutorGender = parseBoolean(params.get('gender')) !== 0 ? 1 : 0;
      }
      // default = visible
      if (params.has('locallist')) {
        this.visible.generatorLocalList = parseBoolean(params.get('locallist')) !== 0 ? 1 : 0;
      }
      if (params.has('nearby')) {
        this.visible.generatorNearby = parseBoolean(params.get('nearby')) !== 0 ? 1 : 0;
      }
      if (params.has('wikimedia')) {
        this.visible.generatorWikimediaCategory = parseBoolean(params.get('wikimedia')) !== 0 ? 1 : 0;
      }
      if (params.has('lexeme')) {
        this.visible.generatorLexeme = parseBoolean(params.get('lexeme')) !== 0 ? 1 : 0;
      }
      if (params.has('externaltools')) {
        this.visible.generatorExternalTools = parseBoolean(params.get('externaltools')) !== 0 ? 1 : 0;
      }
      // default = hidden
      if (params.has('tatoeba')) {
        this.visible.generatorTatoeba = parseBoolean(params.get('tatoeba')) === 1 ? 1 : 0;
      }
      if (params.has('download')) {
        this.visible.downloadLocally = parseBoolean(params.get('download')) === 1 ? 1 : 0;
      }
    },

    removeSelectedLocutor() {
      if (!this.locutor || !Array.isArray(this.locutors)) return

      let selectedLocutorIndex = this.locutors.findIndex((item) => item.id === this.locutor.id)
      this.locutors.splice(selectedLocutorIndex, 1)

      if (selectedLocutorIndex >= this.locutors.length) {
        selectedLocutorIndex = this.locutors.length - 1
      }

      this.selectedLocutorId = this.locutors[selectedLocutorIndex]?.id
    },
    addLanguage() {
      const locutor = this.hasSelectedNewLocuter
        ? this.newLocuter
        : (Array.isArray(this.locutors) ? this.locutors.find((locutor) => locutor.id === this.selectedLocutorId) : undefined)
      if (locutor) {
        locutor.languages_used.push({ proficiency: 2, language_qid: '', place_of_learning: '' })
      }
    },
    removeLanguage(qid) {
      const locutor = this.hasSelectedNewLocuter
        ? this.newLocuter
        : (Array.isArray(this.locutors) ? this.locutors.find((locutor) => locutor.id === this.selectedLocutorId) : undefined)
      if (locutor) {
        locutor.languages_used = locutor.languages_used.filter(
          (language) => language.language_qid !== qid,
        )
      }
    },
    saveLocuter() {

      if (this.hasSelectedNewLocuter) {
        makeAuthenticatedAPICall({
          method: 'POST',
          url: '/locutors/my',
          data: this.locutor,
        })
      } else {
        makeAuthenticatedAPICall({
          method: 'PUT',
          url: '/locutors/my/' + this.selectedLocutorId,
          data: this.locutor,
        })
      }
    },
    addWords(input) {
      if (input.charAt(0) !== '#') {
        input = '#' + input
      }
      const addLineJump = (text) => text.replace(/(#+)/g, '\n$1')

      const split = (text) =>
        text
          .split(/^#/m)
          .map((str) => `#` + str.trim())
          .splice(1)
      const addItemType = split(addLineJump(input)).map((str) => {
        const match = (str.match(/#/g) || []).length // Count the number of '#'
        const type = match === 0 || match === 1 ? 'word' : match === 2 ? 'sentence' : 'poem'
        return { displayAs: str, type }
      })
      
      // Get the highest position value
      const maxPosition = this.items.reduce((max, item) => Math.max(max, item.position || 0), 0)
      
      const removeSharps = addItemType.map((obj, idx) => {
        const indexes = []
        while (true) {
          const index = Math.floor(100000 + Math.random() * 900000)
          if (!this.items.some((item) => item.id === index) && !indexes.includes(index)) {
            indexes.push(index)
            const cleanText = sanitizeDisplayAs(obj.displayAs.replace(/#+/g, '').trim())
            return { 
              id: index, 
              displayAs: cleanText, 
              type: obj.type,
              position: maxPosition + idx + 1
            }
          }
        }
      })
      this.items = this.items.concat(removeSharps)
    },
    addWordsWithQid(text, qid, type = 'word', source = 'wikidata') {
      // Add a single word with its Wikidata QID
      const index = Math.floor(100000 + Math.random() * 900000)
      
      // Ensure unique ID
      if (this.items.some((item) => item.id === index)) {
        return this.addWordsWithQid(text, qid) // retry with new random ID
      }
      
      const cleanText = sanitizeDisplayAs(text.trim())
      
      // Get the highest position value
      const maxPosition = this.items.reduce((max, item) => Math.max(max, item.position || 0), 0)
      
      this.items.push({ 
        id: index, 
        displayAs: cleanText, 
        type: type, 
        qid: qid,
        source: source,
        position: maxPosition + 1
      })
    },
    addWordsArray(input) {
      for (const item of input) {
        this.addWords(item)
      }
    },

    async fetchUserUploads() {
      console.debug('RecordWizardStore.fetchUserUploads() START')
      
      if (!this.locutors || this.locutors.length === 0 || !this.locutors[0]?.name) {
        console.warn('No locutor name available to fetch uploads')
        return
      }

      const username = this.locutors[0].name
      const categoryName = `Category:Lingua Libre pronunciation by ${username}`
      
      console.debug('Fetching uploads for category:', categoryName)
      
      try {
        const uploads = await fetchCategoryFiles(categoryName)
        this.userUploadsAlready = uploads
        console.log("fetchCategoryFiles:", uploads)
        console.debug(`Fetched ${uploads.length} existing uploads for user:`, username)
      } catch (error) {
        console.error('Failed to fetch user uploads:', error)
        this.userUploadsAlready = []
      }
    },
    async filterUserUploadsForCurrentLanguage() {
      if (!this.userUploadsAlready.length || !this.selectedLanguageId || !this.locutors[0]) {
        return [];
      }

      const locutorName = this.locutors[0].name;
      const selectedLocutorName = this.locutor?.name;

      const wantedFilenamesPrefixRegex = locutorName === selectedLocutorName
        ? new RegExp(`^File:LL-${this.selectedLanguageId} \\(.+?\\)-${locutorName}-(.+?)\\.(wav|webm)$`)
        : new RegExp(`^File:LL-${this.selectedLanguageId} \\(.+?\\)-${selectedLocutorName} \\(${locutorName}\\)-(.+?)\\.(wav|webm)$`);
      console.log('Filtering uploads with regex:', wantedFilenamesPrefixRegex);
      return this.userUploadsAlready
        .filter(title => wantedFilenamesPrefixRegex.test(title))
        .map(title => {
          const match = title.match(wantedFilenamesPrefixRegex);
          return match ? match[1] : null;
        })
        .filter(transcription => transcription !== null);
    },
    setDefaultLicense(license) {
      this.newLocuter.license = license
    },
    setWarning(message) {
      this.warning = message
    }
  },
})
